// Class names emitted by a build tool or CSS-in-JS runtime. The numeric part is
// assigned at mount/compile time, so it is not stable across deploys.
const GENERATED_CLASS_PATTERN = /^(jss|sc-|emotion-|e[a-z0-9]{6,})\d*/i;

// Spacing, sizing, colour and display utilities. Bootstrap-style and the
// project's own `f-14px` / `ml-10px` scales.
const UTILITY_CLASS_PATTERN =
  /^(m|p)[trblxy]?-\d|^(f|w|h|gap)-\d|^(text|bg|border|rounded|flex|justify|align|order|col|row|d)-/;

const GENERIC_CLASS_TOKENS = new Set([
  "active",
  "align-items-center",
  "align-items-start",
  "align-self-center",
  "bg-primary",
  "bg-transparent",
  "bg-white",
  "border",
  "border-0",
  "btn",
  "btn-block",
  "btn-elevate",
  "btn-icon",
  "btn-light",
  "btn-primary",
  "card-body",
  "checked",
  "close",
  "col",
  "col-auto",
  "container",
  "content",
  "cursor-pointer",
  "d-block",
  "d-flex",
  "disabled",
  "flex-column",
  "flex-grow-1",
  "float-right",
  "form-control",
  "form-group",
  "icon",
  "justify-content-between",
  "justify-content-center",
  "m-0",
  "mb-0",
  "modal-body",
  "modal-content",
  "modal-dialog",
  "modal-header",
  "mr-10px",
  "mt-2",
  "MuiButtonBase-root",
  "MuiDialog-paper",
  "MuiFormLabel-root",
  "MuiSvgIcon-root",
  "position-relative",
  "preview-container",
  "rounded",
  "row",
  "span",
  "svg-icon",
  "text-center",
  "text-light",
  "text-primary",
  "toolbar",
  "w-100",
]);

export function extractTextValue(value) {
  if (value === null || value === undefined || value === false) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => extractTextValue(item))
      .filter(Boolean)
      .join(" ");
  }

  if (value?.props?.children) {
    return extractTextValue(value.props.children);
  }

  return "";
}

export function sanitizeTestId(value) {
  const normalizedValue = extractTextValue(value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s_-]/g, " ")
    .trim()
    .toLowerCase();

  if (!normalizedValue) {
    return "";
  }

  return normalizedValue
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function buildTestId(...parts) {
  const tokens = parts.flatMap((part) => sanitizeTestId(part).split("-")).filter(Boolean);

  if (!tokens.length) {
    return "";
  }

  return [...new Set(tokens)].join("-");
}

/**
 * Builds a test id from a fixed prefix plus a free-text key such as a filename.
 *
 * buildTestId de-duplicates tokens across every part, which is right when the
 * parts are independent labels but wrong here: a file named "my-file.png" under
 * a "provider-file-card" prefix loses its "file" token, so it collapses onto the
 * same id as "my.png". The key is sanitised on its own and appended verbatim.
 */
export function buildKeyedTestId(prefix, key, ...suffix) {
  const sanitizedKey = sanitizeTestId(key);

  if (!sanitizedKey) {
    return "";
  }

  return [sanitizeTestId(prefix), sanitizedKey, ...suffix.map(sanitizeTestId)]
    .filter(Boolean)
    .join("-");
}

export function resolveTestId(...candidates) {
  for (const candidate of candidates) {
    const testId = sanitizeTestId(candidate);

    if (testId) {
      return testId;
    }
  }

  return "";
}

export function getMeaningfulClassName(element) {
  if (!element?.classList?.length) {
    return "";
  }

  const meaningfulTokens = [...element.classList].filter((token) => {
    if (!token || GENERIC_CLASS_TOKENS.has(token)) {
      return false;
    }

    if (
      token.startsWith("Mui") ||
      token.startsWith("css-") ||
      token.startsWith("makeStyles-") ||
      token.startsWith("kt-")
    ) {
      return false;
    }

    // Build-generated class names. JSS numbers its sheets in mount order, so the
    // same control was jss121 in one deploy and jss17 in another - any id built
    // from one of these changes between builds. Matched by shape rather than by
    // listing names, so a new generator does not silently start leaking again.
    if (GENERATED_CLASS_PATTERN.test(token)) {
      return false;
    }

    // Layout utilities carry no identity - they describe spacing, sizing or
    // colour. These were being filtered one name at a time, which is why
    // mr-10px was excluded but ml-10px leaked into an id.
    if (UTILITY_CLASS_PATTERN.test(token)) {
      return false;
    }

    if (/^\d/.test(token)) {
      return false;
    }

    return token.includes("-") || token.length > 3;
  });

  return meaningfulTokens.join(" ");
}
